import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExtensionRequestDialog = ({ task, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for extension');
      return;
    }

    try {
      setSubmitting(true);
      const config = {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('session_token') || ''}`
        }
      };

      await axios.post(
        `${API}/tasks/${task.id}/request-extension`,
        { reason: reason.trim() },
        config
      );

      toast.success('Extension request submitted');
      setOpen(false);
      setReason('');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error requesting extension:', error);
      toast.error(error.response?.data?.detail || 'Failed to request extension');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
          disabled={task.extension_requested}
        >
          <Clock className="w-4 h-4" />
          {task.extension_requested ? 'Extension Pending' : 'Request Extension'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Deadline Extension</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-slate-600 mb-2">
              <strong>Task:</strong> {task.title}
            </p>
            <p className="text-sm text-slate-600 mb-4">
              Please provide a reason for requesting a deadline extension. The admin will review your request.
            </p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Reason for Extension *
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Unexpected issues, additional requirements, equipment delay..."
              rows={4}
              className="resize-none"
              disabled={submitting}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setReason('');
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !reason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExtensionRequestDialog;
